import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n';
import { useTheme } from '@/themes/themes';
import GoogleSignInButton, { AuthDivider } from '@/components/GoogleSignInButton';

const BG_STYLE = {
  backgroundImage: `
    radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.12) 100%),
    radial-gradient(circle at center, rgba(255,250,235,0.2) 0%, rgba(255,250,235,0.08) 25%, transparent 50%),
    url(/assets/havn_bg_dark.png)
  `,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
};

const inputStyle = {
  width: '100%', padding: '14px 16px', fontSize: '16px',
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '10px', color: '#fff', outline: 'none',
  transition: 'border-color 0.2s',
};

const labelStyle = {
  display: 'block', fontSize: '13px', fontWeight: 600,
  color: 'rgba(255,255,255,0.6)', marginBottom: '6px',
};

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, resetPassword } = useAuth();
  const { t, lang, setLang, languages } = useTranslation();
  const { theme, themeName } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/home', { replace: true });
    } catch (err) {
      setError(err.message === 'Invalid login credentials'
        ? t('login_error')
        : err.message || t('login_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) { setError(t('login_forgot_email_first')); return; }
    setError('');
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      setError(err.message || t('login_reset_failed'));
    }
  };

  return (
    <div style={{ ...BG_STYLE, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', backgroundColor: theme.background, backgroundImage: `radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.12) 100%), url(${theme.backgroundImage || '/assets/Smooth_blue_gradient_backdrop.png'})` }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          width: '100%', maxWidth: '400px',
          background: 'var(--havn-surface)', backdropFilter: 'blur(20px)',
          borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)',
          padding: '40px 32px',
        }}
      >
        {/* Language toggle */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <div style={{ display: 'flex', borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}>
            {languages.map(l => (
              <button key={l.code} onClick={() => setLang(l.code)}
                style={{
                  minWidth: '44px', minHeight: '44px', padding: '10px 16px',
                  fontSize: '14px', fontWeight: lang === l.code ? 700 : 500,
                  background: lang === l.code ? 'var(--havn-primary-10)' : 'transparent',
                  color: lang === l.code ? 'var(--havn-primary)' : 'rgba(255,255,255,0.5)',
                  border: 'none', cursor: 'pointer',
                  lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{l.flag}</button>
            ))}
          </div>
        </div>

        <img src="/havn-logo-white.png" alt="HAVN" style={{ height: '32px', marginBottom: '32px', display: 'block', margin: '0 auto 32px' }} />

        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#fff', textAlign: 'center', marginBottom: '8px' }}>{t('login_title')}</h2>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: '28px' }}>{t('login_subtitle')}</p>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#F87171' }}>
            {error}
          </div>
        )}

        {resetSent && (
          <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#4ADE80' }}>
            {t('login_reset_sent')}
          </div>
        )}

        <GoogleSignInButton
          label={lang === 'fr' ? 'Continuer avec Google' : 'Continue with Google'}
          onError={setError}
        />
        <AuthDivider label={lang === 'fr' ? 'ou' : 'or'} />

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>{t('label_email')}</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder={t('placeholder_email')} required autoComplete="email" autoFocus
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = 'var(--havn-primary-20)'}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
            />
          </div>

          <div style={{ marginBottom: '8px' }}>
            <label style={labelStyle}>{t('label_password')}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder={t('placeholder_password')} required autoComplete="current-password"
                style={{ ...inputStyle, paddingRight: '48px' }}
                onFocus={e => e.target.style.borderColor = 'var(--havn-primary-20)'}
                onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? t('aria_hide_password') : t('aria_show_password')}
                style={{
                  position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                  color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center',
                }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '24px', marginTop: '12px' }}>
            <button type="button" onClick={handleForgotPassword}
              style={{
                background: 'none', border: 'none', color: 'var(--havn-primary)',
                fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                padding: '8px 16px', borderRadius: '8px',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.target.style.background = 'var(--havn-primary-10)'}
              onMouseLeave={e => e.target.style.background = 'none'}
            >
              {t('forgot_password')}
            </button>
          </div>

          <button type="submit" disabled={loading}
            style={{
              width: '100%', padding: '14px', fontSize: '15px', fontWeight: 600,
              background: loading ? 'var(--havn-primary-20)' : 'var(--havn-primary)',
              color: '#000', border: 'none', borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}>
            {loading ? t('login_loading') : t('login_btn')}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
          {t('login_no_account')}{' '}
          <Link to="/signup" style={{ color: 'var(--havn-primary)', textDecoration: 'none', fontWeight: 600 }}>{t('signup_link')}</Link>
        </p>
      </motion.div>
    </div>
  );
}
